import { Badge, Flex, Skeleton, Stack, Text } from '@chakra-ui/react';
import type { DataTableCellContext as CellContext, DataTableColumnDef as ColumnDef } from '@/components/DataTable';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '@/api/User';
import { Status } from '@/api/Status';
import { useAuthContext } from '@/auth/AuthContext';
import ContractTabs from '@/components/ContractTabs';
import Content from '@/components/Content';
import DataTable, { DataTableToolbar } from '@/components/DataTable';
import StatusBadge from '@/components/StatusBadge';
import WaitingSpinner from '@/components/WaitingSpinner';
import { Avatar } from '@/components/ui/avatar';
import { formatDay, formatMinutes } from '@/data/DateTimeUtil';
import { ApprovalSummary, emptyApprovalSummary, hasPendingApprovals } from '@/data/ApprovalSummaryUtil';
import { approvalSummaryService } from '@/service/ApprovalSummaryService';
import { userService } from '@/service/UserService';

const userName = (user: User): string => `${user.firstName} ${user.lastName}`;

const OverviewDirectReports = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [reports, setReports] = useState<User[]>([]);
    const [approvalSummaries, setApprovalSummaries] = useState<Map<number, ApprovalSummary>>(new Map());
    const [approvalTotals, setApprovalTotals] = useState<ApprovalSummary>(emptyApprovalSummary());
    const [isLoading, setIsLoading] = useState(false);
    const currentMonth = useMemo(() => new Date(), []);

    useEffect(() => {
        const currentUserId = user?.data?.id;
        if (!currentUserId) {
            setReports([]);
            setApprovalSummaries(new Map());
            setApprovalTotals(emptyApprovalSummary());
            return;
        }

        setIsLoading(true);
        approvalSummaryService.fetchDirectReportSummaries(currentUserId, approvalSummaryService.monthRange(currentMonth))
            .then(result => {
                setReports(result.reports);
                setApprovalSummaries(result.summaries);
                setApprovalTotals(result.totals);
            })
            .catch(error => {
                console.error('Failed to load direct reports', error);
                setReports([]);
                setApprovalSummaries(new Map());
                setApprovalTotals(emptyApprovalSummary());
            })
            .finally(() => setIsLoading(false));
    }, [currentMonth, user?.data?.id]);

    const columns: ColumnDef<User>[] = useMemo(
        () => [
            {
                id: 'name',
                header: 'Name',
                accessorFn: userName,
                cell: (info: CellContext<User, string>) => {
                    const row = info.row.original;
                    const name = info.getValue();
                    return (
                        <Flex align='center' gap='0.75rem'>
                            <Avatar
                                size='sm'
                                name={name}
                                src={userService.avatarUrl(row)}
                                borderless={false}
                                borderWidth='1px'
                            />
                            <Text fontWeight='semibold'>{name}</Text>
                        </Flex>
                    );
                },
            },
            {
                id: 'email',
                header: 'E-mail',
                accessorKey: 'email',
            },
            {
                id: 'status',
                header: 'Status',
                accessorKey: 'status',
                cell: (info: CellContext<User, Status>) => <StatusBadge status={info.getValue()} />,
            },
            {
                id: 'pendingTimesheets',
                header: 'Offene Stunden',
                accessorFn: (row) => approvalSummaries.get(row.id)?.timesheetMinutes ?? 0,
                cell: (info: CellContext<User, number>) => {
                    const minutes = info.getValue();
                    return minutes > 0
                        ? <Badge colorPalette='yellow' variant='subtle'>{formatMinutes(minutes)} h</Badge>
                        : <Text color='fg.muted'>-</Text>;
                },
            },
            {
                id: 'pendingAbsences',
                header: 'Offene Absenzen',
                accessorFn: (row) => approvalSummaries.get(row.id)?.absenceDays ?? 0,
                cell: (info: CellContext<User, number>) => {
                    const days = info.getValue();
                    return days > 0
                        ? <Badge colorPalette='purple' variant='subtle'>{formatDay(days)} Tg</Badge>
                        : <Text color='fg.muted'>-</Text>;
                },
            },
        ],
        [approvalSummaries]
    );

    return (
        <Content title='Direkte Berichte' description='Offene Genehmigungen im aktuellen Monat.'>
            <Stack gap='6' minW='0'>
                {!isLoading && reports.length > 0 && hasPendingApprovals(approvalTotals) && (
                    <ContractTabs
                        summary={null}
                        monthMinutes={null}
                        month={currentMonth}
                        approvalSummary={approvalTotals}
                        showContractStats={false}
                    />
                )}
                <DataTableToolbar resultCount={isLoading ? undefined : reports.length}>{null}</DataTableToolbar>
                {isLoading ? (
                    <Stack py={5}>
                        <WaitingSpinner size='xl' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                    </Stack>
                ) : reports.length === 0 ? (
                    <Text textAlign='center' color='fg.muted'>Keine direkten Berichte gefunden.</Text>
                ) : (
                    <DataTable
                        columns={columns}
                        data={reports}
                        initialSortByColumn={hasPendingApprovals(approvalTotals) ? ['pendingTimesheets', 'pendingAbsences', 'name'] : 'name'}
                        initialSortByDesc={hasPendingApprovals(approvalTotals) ? [true, true, false] : false}
                        hideColumnsOnXs={['email', 'status']}
                        onRowClick={row => navigate(`/admin/users/direct-reports/${row.id}`)}
                    />
                )}
            </Stack>
        </Content>
    );
};

export default OverviewDirectReports;
